import React, { useState, useEffect } from "react";
import { Box, Typography, Divider } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CustomContentDialog from "../../shared/dialog/CustomContentDialog";
import CustomTextField from "../../shared/textFields/CustomTextField";

export default function PosVendaModal({ 
  open, 
  onClose, 
  projectData, 
  onConfirm,
  loading 
}) {
  const client = projectData?.clients;
  const isInternal = client?.internalClient === 1;
  
  // Cliente final é o proprietário (nome do projeto)
  const clientName = projectData?.name;
  const [plantName, setPlantName] = useState(projectData?.name || "");
  const [validationKey, setValidationKey] = useState(0);

  // Atualiza o nome quando projectData mudar
  useEffect(() => {
    setPlantName(projectData?.name || "");
  }, [projectData?.name]);

  // Força re-validação quando projectData mudar
  useEffect(() => {
    if (open) {
      setValidationKey(prev => prev + 1);
    }
  }, [open, projectData?.city, projectData?.state]);

  // Endereço da instalação (endereço do projeto)
  const address = {
    street: projectData?.street,
    number: projectData?.number,
    addressDetails: projectData?.addressDetails,
    district: projectData?.district,
    city: projectData?.city,
    state: projectData?.state,
    zipCode: projectData?.zipCode,
  };

  // Validação de campos obrigatórios
  const missingFields = [];
  if (!address.city) missingFields.push("Cidade");
  if (!address.state) missingFields.push("Estado");
  
  const hasMissingFields = missingFields.length > 0;

  const content = (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Revise as informações que serão enviadas para o sistema de Pós-Vendas:
      </Typography>

      {!isInternal ? (
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: "#FEF2F2", 
            borderRadius: 2,
            border: "1px solid #FECACA",
            display: "flex",
            alignItems: "center",
            gap: 1.5
          }}
        >
          <ErrorOutlineIcon sx={{ color: "#DC2626" }} />
          <Typography variant="body2" color="#DC2626">
            Este projeto não é um projeto JBE interno e não pode ser cadastrado no Pós-Vendas.
          </Typography>
        </Box>
      ) : hasMissingFields ? (
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: "#FEF2F2", 
            borderRadius: 2,
            border: "1px solid #FECACA",
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5
          }}
        >
          <ErrorOutlineIcon sx={{ color: "#DC2626", mt: 0.5 }} />
          <Box>
            <Typography variant="body2" color="#DC2626" fontWeight={600} sx={{ mb: 1 }}>
              Dados obrigatórios não preenchidos
            </Typography>
            <Typography variant="body2" color="#DC2626">
              O projeto precisa ter os seguintes campos preenchidos para ser cadastrado no Pós-Vendas:
            </Typography>
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
              {missingFields.map((field) => (
                <Typography key={field} component="li" variant="body2" color="#DC2626">
                  {field}
                </Typography>
              ))}
            </Box>
            <Typography variant="body2" color="#DC2626" sx={{ mt: 1.5 }}>
              Por favor, complete o cadastro do projeto antes de enviar para o Pós-Vendas.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Cliente (Proprietário)
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {clientName || "—"}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Número do Projeto
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {projectData?.projectNumber || "—"}
            </Typography>
          </Box>

          <Divider />

          <CustomTextField
            params={{
              value: plantName,
              onChange: (e) => setPlantName(e.target.value),
              error: false,
              errorLabel: "",
            }}
            disabled={loading}
            label="Nome da Usina"
            width="100%"
            margin={0}
          />

          <Divider />

          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Potência Instalada
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {projectData?.power ? `${projectData.power} kWp` : "—"}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Data de Homologação
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {projectData?.approvalDate 
                  ? new Date(projectData.approvalDate).toLocaleDateString('pt-BR')
                  : "—"}
              </Typography>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Endereço
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {address?.city || address?.state || address?.street ? (
                <>
                  {address.street && address.number && (
                    <>
                      {address.street}, {address.number}
                      {address.addressDetails && ` - ${address.addressDetails}`}
                      <br />
                    </>
                  )}
                  {address.district && `${address.district}, `}
                  {address.city && `${address.city}`}
                  {address.state && ` - ${address.state}`}
                  {address.zipCode && (
                    <>
                      <br />
                      CEP: {address.zipCode}
                    </>
                  )}
                </>
              ) : (
                "—"
              )}
            </Typography>
          </Box>

          <Box 
            sx={{ 
              mt: 2,
              p: 2, 
              bgcolor: "#FEF9E7", 
              borderRadius: 2,
              border: "1px solid #F4D03F"
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>Atenção:</strong> Após o cadastro, a usina estará disponível no sistema de Pós-Vendas para gestão de manutenção.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <CustomContentDialog
      open={open}
      handleClickClose={onClose}
      title="Cadastrar Usina no Pós-Vendas"
      content={content}
      handleConfirmationClick={() => onConfirm(plantName)}
      confirmationClickText="Cadastrar Usina"
      requestLoading={loading}
      requestLoadingText="Cadastrando..."
      width={600}
      height="auto"
      firstButtonColor="warning"
      confirmationDisabled={!isInternal || !plantName.trim() || hasMissingFields}
    />
  );
}
